import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, Phone, Star, MapPin } from "lucide-react";
import { AVIS_GOOGLE, GOOGLE_REVIEWS_URL } from "../data/avisGoogle";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_azure-seo-boost/artifacts/v4b4r58r_logo%20AT.png";

const GoogleIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

function Temoignages() {
  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Avis Clients Google Vérifiés | Dératisation & Désinsectisation Nice (06) | Azur Tech</title>
        <meta name="description" content="Découvrez les avis Google vérifiés de nos clients à Nice et dans les Alpes-Maritimes (06). Note 5/5. Dératisation, désinsectisation, punaises de lit." />
        <link rel="canonical" href="https://azurtech06.fr/temoignages" />
      </Helmet>
      {/* Header */}
      <header className="bg-white shadow-md fixed w-full top-0 z-50">
        <div className="bg-[#0f172a] text-white py-1.5 text-xs md:text-sm" data-testid="trust-banner">
          <div className="container mx-auto px-4 flex items-center space-x-2 md:space-x-3 overflow-hidden">
            <span className="truncate">Entreprise certifiée • SIREN vérifié 821 012 630</span>
            <span className="text-gray-500">•</span>
            <a href={GOOGLE_REVIEWS_URL} target="_blank" rel="noopener noreferrer" data-testid="trust-banner-google-link" className="flex items-center text-yellow-400 hover:text-yellow-300 font-semibold flex-shrink-0">⭐ 5/5 Avis Google</a>
          </div>
        </div>
        <nav className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center space-x-3">
            <img src={LOGO_URL} alt="Azur Tech Logo" className="h-12 w-auto" />
            <div>
              <h2 className="text-xl font-bold text-cyan-600">Azur Tech</h2>
              <p className="text-xs text-gray-600">Expert 3D</p>
            </div>
          </Link>
          <a 
            href="tel:0764079535" 
            className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-bold flex items-center space-x-2 transition-all shadow-lg hover:shadow-xl"
          >
            <Phone className="w-5 h-5" />
            <span className="hidden md:inline">07 64 07 95 35</span>
          </a>
        </nav>
      </header>

      {/* Content */}
      <div className="pt-32 md:pt-36 pb-16">
        <div className="container mx-auto px-4">
          {/* Breadcrumb */}
          <div className="mb-8">
            <Link to="/" className="text-cyan-600 hover:underline inline-flex items-center"><ArrowLeft className="w-4 h-4 mr-1" />Accueil</Link>
            <span className="mx-2 text-gray-400">/</span>
            <span className="text-gray-600">Avis clients</span>
          </div>

          {/* Page Title */}
          <div className="mb-12 text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Avis Clients Google Vérifiés
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Tous les avis ci-dessous proviennent de notre fiche Google Business officielle et peuvent y être vérifiés.
            </p>
            <div className="flex items-center justify-center mt-6 space-x-2">
              <div className="flex">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star key={star} className="w-6 h-6 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
              <span className="text-2xl font-bold text-gray-900">5/5</span>
              <span className="text-gray-600">({AVIS_GOOGLE.length} avis)</span>
              <GoogleIcon className="w-6 h-6" />
            </div>
          </div>

          {/* Avis Google Grid */}
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
              {AVIS_GOOGLE.map((avis, idx) => (
                <div
                  key={idx}
                  className="bg-white rounded-xl shadow-lg p-6 hover:shadow-2xl transition-shadow"
                  data-testid={`testimonial-${idx}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-start">
                      <div className="w-12 h-12 rounded-full bg-cyan-600 text-white flex items-center justify-center font-bold text-lg flex-shrink-0">
                        {avis.nom.charAt(0)}
                      </div>
                      <div className="ml-3">
                        <h3 className="font-bold text-gray-900">{avis.nom}</h3>
                        <div className="flex items-center text-sm text-gray-600 mt-1">
                          <MapPin className="w-3 h-3 mr-1" />
                          {avis.ville}
                        </div>
                      </div>
                    </div>
                    <GoogleIcon />
                  </div>

                  <div className="flex items-center mb-3">
                    {[...Array(avis.note)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    ))}
                    <span className="ml-2 text-sm text-gray-600">{avis.date}</span>
                  </div>

                  <div className="mb-3">
                    <span className="inline-block bg-blue-100 text-blue-700 text-xs px-3 py-1 rounded-full font-semibold">
                      Avis Google vérifié
                    </span>
                  </div>

                  <p className="text-gray-700 leading-relaxed">
                    "{avis.texte}"
                  </p>
                </div>
              ))}
            </div>

            {/* Google Reviews CTA */}
            <div className="bg-gradient-to-br from-cyan-600 to-blue-600 rounded-xl p-8 text-center text-white">
              <h3 className="text-3xl font-bold mb-4">
                Vérifiez nos avis sur Google
              </h3>
              <p className="text-xl mb-6">
                Tous nos avis sont consultables sur notre fiche Google Business officielle
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href={GOOGLE_REVIEWS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="temoignages-google-btn"
                  className="bg-white text-cyan-600 px-8 py-4 rounded-lg font-bold hover:bg-gray-100 transition-all inline-flex items-center justify-center"
                >
                  ⭐ Voir nos Avis Google
                </a>
                <a
                  href="tel:0764079535"
                  className="bg-cyan-700 text-white px-8 py-4 rounded-lg font-bold hover:bg-cyan-800 transition-all inline-flex items-center justify-center"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  07 64 07 95 35
                </a>
              </div>
            </div>

            {/* Trust indicators */}
            <div className="mt-12 grid md:grid-cols-3 gap-6 text-center">
              <div className="bg-white rounded-lg p-6 shadow-md">
                <div className="text-4xl font-bold text-cyan-600 mb-2">5/5</div>
                <p className="text-gray-600">Note Google</p>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-md">
                <div className="text-4xl font-bold text-cyan-600 mb-2">SIREN</div>
                <p className="text-gray-600">821 012 630 — entreprise vérifiée</p>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-md">
                <div className="text-4xl font-bold text-cyan-600 mb-2">24h/24</div>
                <p className="text-gray-600">Disponibilité 7j/7</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Temoignages;
